import { useQuery } from '@tanstack/react-query';
import React from 'react';
import GenericDataProvider from '@/common/api/GenericDataProvider';
import type {
  ReferenceOption,
  UseReferenceSearchParams,
  UseReferenceSearchResult,
} from './types';

const DEBOUNCE_MS = 250;

const listItemsFromResponse = (payload: unknown): ReferenceOption[] => {
  const root = payload as {
    data?: unknown[] | { data?: ReferenceOption[] };
  } | undefined;
  if (!root?.data) return [];
  if (Array.isArray(root.data)) {
    return root.data.map((row) => {
      const item = row as { id?: string; title?: string | null };
      return { id: item.id ?? '', title: item.title };
    }).filter((row) => row.id);
  }
  const nested = root.data as { data?: ReferenceOption[] };
  return (nested.data ?? []).map((row) => ({
    id: row.id,
    title: row.title,
  }));
};

/**
 * Server-backed reference search with debounce, aligned with admin useData list requests.
 */
export const useReferenceSearch = ({
  objectName,
  query,
  fixedFilter = {},
  searchField = 'title',
  limit = 8,
  excludeIds = [],
  enabled = true,
}: UseReferenceSearchParams): UseReferenceSearchResult => {
  const [debouncedQuery, setDebouncedQuery] = React.useState(query.trim());

  React.useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedQuery(query.trim());
    }, DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [query]);

  const excludeSet = React.useMemo(() => new Set(excludeIds), [excludeIds]);
  // Callers pass a fresh object each render. Key on its contents so that
  // does not cancel an in-flight search and leave the spinner up.
  const fixedFilterKey = JSON.stringify(fixedFilter);

  const queryKey = React.useMemo(
    () => [
      'reference-search',
      objectName,
      debouncedQuery,
      fixedFilterKey,
      searchField,
      limit,
    ],
    [objectName, debouncedQuery, fixedFilterKey, searchField, limit],
  );

  const searchEnabled = enabled && debouncedQuery.length > 0;

  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey,
    enabled: searchEnabled,
    queryFn: async () => {
      const dataProvider = new GenericDataProvider();
      const filter: Record<string, unknown> = { ...fixedFilter };
      filter[searchField] = debouncedQuery;
      return dataProvider.getList(objectName, {
        page: 1,
        pageSize: limit,
        sort: searchField,
        filter,
        queryParams: {},
      });
    },
  });

  const options = React.useMemo(() => {
    if (!searchEnabled) return [];
    return listItemsFromResponse(data).filter((option) => !excludeSet.has(option.id));
  }, [data, excludeSet, searchEnabled]);

  return {
    options,
    isLoading: searchEnabled && isLoading,
    error: searchEnabled ? error : null,
    retry: () => {
      refetch().catch(() => undefined);
    },
  };
};

export default useReferenceSearch;
